export const moduleSlugs = [
  "platform-base",
  "intelligence",
  "vault",
  "operate",
  "shop",
  "forge",
] as const;

export type ModuleSlug = (typeof moduleSlugs)[number];

type IntelligenceContext = {
  name: string;
  headline: string;
  helpsWith: string[];
  drawsOn: string[];
  humanAuthority: string;
  aiUsage: string;
};

type ModuleRecord = {
  id: ModuleSlug;
  order: number;
  accent: string;
  eyebrow: string;
  title: string;
  valueStatement: string;
  outcome: string;
  workflow: string[];
  intelligenceInContext: IntelligenceContext;
  evidenceProduced: string[];
  regulatory: {
    anchors: string[];
    included: string[];
    readinessServices: string[];
  };
  pricingId: string;
  showExtensions: boolean;
  sharedBy?: ModuleSlug[];
  dataGovernance?: {
    headline: string;
    points: string[];
  };
  moduleBreakdowns?: Array<{
    moduleId: ModuleSlug;
    iq: string[];
    helpsWith: string[];
    humanAuthority: string;
  }>;
  groups?: Record<
    string,
    {
      label: string;
      description: string;
      items: string[];
    }
  >;
  externalParticipants?: string;
};

type FeatureRecord = {
  name: string;
  summary: string;
};

type FeaturesFile = {
  moduleId: ModuleSlug;
  features: FeatureRecord[];
};

type RoleGroups = {
  primary?: string[];
  supporting?: string[];
  governance?: string[];
  external?: string[];
};

type MappingsFile = {
  moduleId: ModuleSlug;
  roles: string[] | RoleGroups;
  orgTypes: string[];
  extensions: string[];
  integrations: string[];
};

type RelationshipsFile = {
  moduleId: ModuleSlug;
  worksWith: Array<{
    type: string;
    target: string;
    label: string;
  }>;
};

type SharedRole = { id: string; name: string };
type SharedOrgType = { id: string; name: string };
type SharedExtension = { id: string; name: string; group: string; summary: string; pricingId: string };
type SharedIntegration = { id: string; name: string };
type RegulatoryAnchor = { id: string; label: string };

type PricingEntry = {
  basis: string[];
  headline: string;
  subline?: string;
  considerations: Array<{ factor: string; detail: string }>;
};

type PricingFile = {
  entries: Record<string, PricingEntry>;
  aiCreditExplainer: {
    title: string;
    points: string[];
    examples: Array<{ activity: string; approx: string }>;
  };
  extensionPricing: Record<string, string>;
};

function slugFromPath(path: string): ModuleSlug {
  const match = path.match(/modules\/([^/]+)\//);
  if (!match) {
    throw new Error(`Unable to resolve module slug from path: ${path}`);
  }
  return match[1] as ModuleSlug;
}

const moduleFiles = import.meta.glob("./content/modules-viewer/modules/*/module.json", { eager: true });
const featureFiles = import.meta.glob("./content/modules-viewer/modules/*/features.json", { eager: true });
const mappingFiles = import.meta.glob("./content/modules-viewer/modules/*/mappings.json", { eager: true });
const relationshipFiles = import.meta.glob("./content/modules-viewer/modules/*/relationships.json", { eager: true });

const modulesBySlug = {} as Record<ModuleSlug, ModuleRecord>;
Object.entries(moduleFiles).forEach(([path, file]) => {
  modulesBySlug[slugFromPath(path)] = (file as { default: ModuleRecord }).default;
});

const featuresBySlug = {} as Record<ModuleSlug, FeatureRecord[]>;
Object.entries(featureFiles).forEach(([path, file]) => {
  const content = (file as { default: FeaturesFile }).default;
  featuresBySlug[slugFromPath(path)] = content.features;
});

const mappingsBySlug = {} as Record<ModuleSlug, MappingsFile>;
Object.entries(mappingFiles).forEach(([path, file]) => {
  mappingsBySlug[slugFromPath(path)] = (file as { default: MappingsFile }).default;
});

const relationshipsBySlug = {} as Record<ModuleSlug, RelationshipsFile["worksWith"]>;
Object.entries(relationshipFiles).forEach(([path, file]) => {
  const content = (file as { default: RelationshipsFile }).default;
  relationshipsBySlug[slugFromPath(path)] = content.worksWith;
});

const rolesShared = ((
  import.meta.glob("./content/modules-viewer/shared/roles.json", { eager: true })[
    "./content/modules-viewer/shared/roles.json"
  ] as { default: { roles: SharedRole[] } }
).default.roles);

const orgTypesShared = ((
  import.meta.glob("./content/modules-viewer/shared/org-types.json", { eager: true })[
    "./content/modules-viewer/shared/org-types.json"
  ] as { default: { orgTypes: SharedOrgType[] } }
).default.orgTypes);

const extensionsShared = ((
  import.meta.glob("./content/modules-viewer/shared/extensions.json", { eager: true })[
    "./content/modules-viewer/shared/extensions.json"
  ] as { default: { extensions: SharedExtension[] } }
).default.extensions);

const integrationsShared = ((
  import.meta.glob("./content/modules-viewer/shared/integrations.json", { eager: true })[
    "./content/modules-viewer/shared/integrations.json"
  ] as { default: { integrations: SharedIntegration[]; systemOfRecordStatement: string } }
).default);

const regulatoryShared = ((
  import.meta.glob("./content/modules-viewer/shared/regulatory.json", { eager: true })[
    "./content/modules-viewer/shared/regulatory.json"
  ] as { default: { disclaimer: string; anchors: RegulatoryAnchor[] } }
).default);

const pricing = ((
  import.meta.glob("./content/modules-viewer/pricing/public-pricing.json", { eager: true })[
    "./content/modules-viewer/pricing/public-pricing.json"
  ] as { default: PricingFile }
).default);

export const modulesViewerContent = {
  moduleSlugs,
  modulesBySlug,
  featuresBySlug,
  mappingsBySlug,
  relationshipsBySlug,
  rolesById: new Map(rolesShared.map((role) => [role.id, role.name])),
  orgTypesById: new Map(orgTypesShared.map((orgType) => [orgType.id, orgType.name])),
  extensionsById: new Map(extensionsShared.map((extension) => [extension.id, extension])),
  integrationsById: new Map(integrationsShared.integrations.map((integration) => [integration.id, integration.name])),
  integrationsSystemOfRecordStatement: integrationsShared.systemOfRecordStatement,
  regulatoryById: new Map(regulatoryShared.anchors.map((anchor) => [anchor.id, anchor.label])),
  regulatoryDisclaimer: regulatoryShared.disclaimer,
  pricingEntriesById: pricing.entries,
  aiCreditExplainer: pricing.aiCreditExplainer,
  extensionPricingById: pricing.extensionPricing,
};

